/* Mirrors Library corpuses into the relational backend (Matter + Email rows),
   which is the single source of truth the app boots from. Surfaces failures
   and import warnings through the persist-error banner. */
import {
  createEmail, deleteEmail, importEmails, updateEmail,
  type ImportEmailInput,
} from "../api/emails";
import { createMatter, deleteMatter, listMatters, updateMatter } from "../api/matters";
import { notifyPersistError } from "../api/client";
import type { CorpusSet, SimAttachment, SimEmail } from "../types";

export function toImportInput(e: SimEmail): ImportEmailInput {
  return {
    from: e.from,
    fromName: e.fromName,
    to: e.to || [],
    cc: e.cc || [],
    subject: e.subject,
    time: e.time,
    raw: e.raw,
    simMeta: { localId: e.id, time: e.time, expect: e.expect, r: e.r },
    attachments: (e.attachments || []).map((a) => ({
      filename: a.filename,
      doc_type: a.doc_type,
      version: a.version,
      content: a.content,
      fileData: a.fileData,
    })),
  };
}

export interface ServerAttachmentRef {
  id: string;
  filename: string;
  version: number;
}

/** Marks local attachments with their relational ids (matched by filename+version). */
export function applyAttachmentServerIds(
  atts: SimAttachment[] | undefined,
  rows: ServerAttachmentRef[] | undefined,
): SimAttachment[] | undefined {
  if (!atts || !rows || !rows.length) return atts;
  const remaining = [...rows];
  return atts.map((a) => {
    const i = remaining.findIndex((r) => r.filename === a.filename && r.version === a.version);
    if (i < 0) return a;
    const [row] = remaining.splice(i, 1);
    return { ...a, serverId: row.id };
  });
}

export interface CorpusSyncResult {
  matterId: string;
  /** SimEmail.id → relational Email.id for emails created by this sync. */
  serverIds: Record<number, string>;
  /** SimEmail.id → created attachment rows (for original-file downloads). */
  serverAtts?: Record<number, ServerAttachmentRef[]>;
}

/* Ensures the corpus has a Matter and that every local email without a
   serverId is imported. Returns null (after notifying) on failure. */
export async function syncCorpusToServer(corpus: CorpusSet): Promise<CorpusSyncResult | null> {
  let matterId = corpus.matterId || null;
  if (!matterId) {
    const matter = await createMatter({ name: corpus.name, status: "active" });
    if (!matter) {
      notifyPersistError(
        `Failed to sync corpus "${corpus.name}" to the backend — matter could not be created. Recent changes are not persisted.`,
      );
      return null;
    }
    matterId = matter.id;
  }
  const pending = corpus.emails.filter((e) => !e.serverId);
  const serverIds: Record<number, string> = {};
  const serverAtts: Record<number, ServerAttachmentRef[]> = {};
  if (pending.length) {
    const r = await importEmails(matterId, pending.map(toImportInput));
    if (!r || !r.ok) {
      notifyPersistError(
        `Failed to sync corpus "${corpus.name}" — ${(r && r.error) || "email import failed"}. Recent changes are not persisted.`,
      );
      // The matter (if just created) is kept so a retry can resume.
      return corpus.matterId ? null : { matterId, serverIds };
    }
    pending.forEach((e, i) => {
      if (r.ids[i]) serverIds[e.id] = r.ids[i];
      if (r.attachments?.[i]?.length) serverAtts[e.id] = r.attachments[i];
    });
    if (r.warnings?.length) {
      const detail = r.warnings
        .slice(0, 3)
        .map((w) => `email #${pending[w.index]?.id ?? w.index + 1}: ${w.warning}`)
        .join("; ");
      const more = r.warnings.length > 3 ? ` (+${r.warnings.length - 3} more)` : "";
      notifyPersistError(`Corpus "${corpus.name}" imported with warnings — ${detail}${more}`);
      return { matterId, serverIds, serverAtts };
    }
  }
  notifyPersistError(null);
  return { matterId, serverIds, serverAtts };
}

/* Reconciles local corpuses against the server's matter list: clears matterId
   for matters deleted server-side, then syncs anything unsynced. Returns the
   per-corpus results keyed by corpus id (only corpuses that changed). */
export async function reconcileCorpuses(
  corpuses: CorpusSet[],
): Promise<Record<string, CorpusSyncResult>> {
  const matters = await listMatters();
  const known = new Set(matters.map((m) => m.id));
  const out: Record<string, CorpusSyncResult> = {};
  for (const c of corpuses) {
    const stale = c.matterId && !known.has(c.matterId);
    const target: CorpusSet = stale
      ? { ...c, matterId: null, emails: c.emails.map((e) => ({ ...e, serverId: undefined })) }
      : c;
    if (target.matterId && target.emails.every((e) => e.serverId)) continue;
    const r = await syncCorpusToServer(target);
    if (r) out[c.id] = r;
    else if (stale) out[c.id] = { matterId: "", serverIds: {} };
  }
  return out;
}

export function applySyncResult(c: CorpusSet, r: CorpusSyncResult): CorpusSet {
  return {
    ...c,
    matterId: r.matterId || null,
    emails: c.emails.map((e) =>
      r.serverIds[e.id]
        ? {
            ...e,
            serverId: r.serverIds[e.id],
            attachments: applyAttachmentServerIds(e.attachments, r.serverAtts?.[e.id]),
          }
        : r.matterId
          ? e
          : { ...e, serverId: undefined },
    ),
  };
}

/* ---- single-email mirroring for Library edits ---- */

export interface MirroredEmail {
  id: string;
  attachments: ServerAttachmentRef[];
}

function toAttachmentRefs(
  atts: Array<{ id: string; filename: string; version: number }> | undefined,
): ServerAttachmentRef[] {
  return (atts || []).map((a) => ({ id: a.id, filename: a.filename, version: a.version }));
}

export async function mirrorEmailCreate(
  matterId: string,
  email: SimEmail,
): Promise<MirroredEmail | null> {
  const created = await createEmail(matterId, toImportInput(email));
  if (!created) {
    notifyPersistError(
      `Failed to save email "${email.subject || "(no subject)"}" to the backend. Recent changes are not persisted.`,
    );
    return null;
  }
  notifyPersistError(null);
  return { id: created.id, attachments: toAttachmentRefs(created.attachments) };
}

/* Replacing an email's attachments creates new rows server-side, so callers
   must re-apply the returned refs or local serverIds go stale. */
export async function mirrorEmailUpdate(email: SimEmail): Promise<ServerAttachmentRef[] | null> {
  if (!email.serverId) return null;
  const updated = await updateEmail(email.serverId, toImportInput(email));
  if (!updated) {
    notifyPersistError(
      `Failed to save email "${email.subject || "(no subject)"}" to the backend. Recent changes are not persisted.`,
    );
    return null;
  }
  notifyPersistError(null);
  return toAttachmentRefs(updated.attachments);
}

export async function mirrorEmailDelete(email: SimEmail): Promise<void> {
  if (!email.serverId) return;
  const ok = await deleteEmail(email.serverId);
  if (!ok) {
    notifyPersistError(
      `Failed to delete email "${email.subject || "(no subject)"}" from the backend.`,
    );
  } else notifyPersistError(null);
}

export async function mirrorCorpusDelete(corpus: CorpusSet): Promise<void> {
  if (!corpus.matterId) return;
  const ok = await deleteMatter(corpus.matterId);
  if (!ok) {
    notifyPersistError(`Failed to delete matter for corpus "${corpus.name}" from the backend.`);
  } else notifyPersistError(null);
}

const renameTimers: Record<string, ReturnType<typeof setTimeout>> = {};
export function mirrorCorpusRename(matterId: string, name: string): void {
  clearTimeout(renameTimers[matterId]);
  renameTimers[matterId] = setTimeout(() => {
    void updateMatter(matterId, { name }).then((m) => {
      if (!m) notifyPersistError(`Failed to rename matter "${name}" on the backend.`);
    });
  }, 800);
}
